import React, { useState, useEffect } from 'react';
import { Trash2, Eye, Edit, Globe, ChevronDown, Info, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import CreatePageForm from '@/components/forms/CreatePageForm';
import FullPageEditorModal from '@/components/FullPageEditorModal';

interface WebPage {
  id: string;
  title: string;
  slug: string;
  category: string;
  lastUpdated: string;
  status: 'Published' | 'Draft' | 'Archived';
  author: string;
}

const slugMapping: Record<string, string> = {
  '/': 'home',
  '/services': 'services',
  '/how-it-works': 'how-it-works',
  '/for/owners': 'homeowners',
  '/for/landlords': 'landlords',
  '/about': 'about',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/contact': 'contact',
  '/faq': 'faq'
};

const getPageSectionSlug = (websitePageSlug: string): string | null => {
  return slugMapping[websitePageSlug] || null;
};

const AdminWebsitePagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editingPageForModal, setEditingPageForModal] = useState<{ slug: string; name: string } | null>(null);
  const [pages, setPages] = useState<WebPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.request('/admin/website/pages', 'GET');
      if (response.success && Array.isArray(response.data)) {
        const mappedPages = response.data.map((page: any) => ({
          id: page.id,
          title: page.title,
          slug: page.slug,
          category: page.category || 'Public',
          lastUpdated: page.published_at || new Date().toISOString().split('T')[0],
          status: (page.status || 'draft').charAt(0).toUpperCase() + (page.status || 'draft').slice(1) as 'Published' | 'Draft' | 'Archived',
          author: page.created_by || 'System'
        }));
        setPages(mappedPages);
      } else {
        setError(response.message || 'Failed to fetch pages');
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch website pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tabs = ['All', 'Published', 'Draft', 'Archived'];

  const filteredPages = activeTab === 'All' 
    ? pages 
    : pages.filter(p => p.status === activeTab);

  const totalPages = pages.length;
  const publishedCount = pages.filter(p => p.status === 'Published').length;
  const draftCount = pages.filter(p => p.status === 'Draft').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Published':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-orange-100 text-orange-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler: Create page
  const handleCreatePage = async (pageData: any) => {
    try {
      const response = await apiService.request('/admin/website/pages/create', 'POST', {
        ...pageData,
        createdBy: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Page created');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Open edit mode
  const handleOpenEdit = (pageId: string) => {
    setEditingPageId(pageId);
    setIsEditMode(true);
  };

  // Handler: Edit page
  const handleEditPage = async (pageId: string, pageData: any) => {
    try {
      const response = await apiService.request(`/admin/website/pages/${pageId}/edit`, 'PUT', {
        title: pageData.title,
        slug: pageData.slug,
        content: pageData.content,
        meta_description: pageData.meta_description,
        status: pageData.status,
        modifiedBy: localStorage.getItem('adminId'),
        modifiedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Page updated');
        setIsEditMode(false);
        setEditingPageId(null);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: View page
  const handleViewPage = (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      alert(`Page: ${page.title}\nSlug: ${page.slug}\nStatus: ${page.status}`);
    }
  };

  // Handler: Delete page
  const handleDeletePage = async (pageId: string) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      const response = await apiService.request(`/admin/website/pages/${pageId}/delete`, 'DELETE', {
        deletedBy: localStorage.getItem('adminId'),
        deletedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Page deleted');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Publish page
  const handlePublishPage = async (pageId: string) => {
    try {
      const response = await apiService.request(`/admin/website/pages/${pageId}/publish`, 'POST', {
        publishedBy: localStorage.getItem('adminId'),
        publishedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Page published');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export pages
  const handleExportPages = async () => {
    try {
      const response = await apiService.request('/admin/website/pages/export', 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `website-pages-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Content exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <AdminLayout>
      <div className="flex-1 overflow-auto">
        {isCreateMode || isEditMode ? (
          <CreatePageForm
            onCancel={() => {
              setIsCreateMode(false);
              setIsEditMode(false);
              setEditingPageId(null);
            }}
            mode={isEditMode ? 'edit' : 'create'}
            pageId={editingPageId}
            initialData={isEditMode ? pages.find(p => p.id === editingPageId) : undefined}
            onSubmit={isEditMode ? handleEditPage : handleCreatePage}
          />
        ) : (
          <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Website Pages</h1>
          <p className="text-gray-600 mb-6">Manage static content and legal pages on the Virewire platform</p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase mb-1 font-semibold">Total Pages</p>
              <p className="text-4xl font-bold text-gray-900">{totalPages}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase mb-1 font-semibold">Live Content</p>
              <p className="text-4xl font-bold text-teal-600">{publishedCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase mb-1 font-semibold">In Draft</p>
              <p className="text-4xl font-bold text-orange-600">{draftCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase mb-1 font-semibold">Status</p>
              <p className="text-4xl font-bold text-gray-900">{loading ? '...' : 'Ready'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-teal-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600"></div>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            CATEGORY: ALL
            <ChevronDown className="w-4 h-4" />
          </button>
          <input
            type="text"
            placeholder="Search pages..."
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          <button
            onClick={() => setIsCreateMode(true)}
            className="ml-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            + Create Page
          </button>
        </div>

        {/* Table */}
        <Card className="mb-6">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">Loading pages...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center bg-red-50">
                <p className="text-red-700 font-medium">❌ Error: {error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : filteredPages.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">No pages found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="py-3 px-4 text-left">
                          <input type="checkbox" className="rounded" />
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Page Title</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Slug</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Last Updated</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Author</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPages.map((page) => (
                        <tr key={page.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <input type="checkbox" className="rounded" />
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm font-medium text-gray-900">{page.title}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 flex items-center gap-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            {page.slug}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{page.category}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{page.lastUpdated}</td>
                          <td className="py-3 px-4">
                            <Badge className={getStatusColor(page.status)}>
                              {page.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-700 font-semibold">
                                {page.author ? page.author.charAt(0) : 'S'}
                              </div>
                              <span className="text-sm text-gray-600">{page.author || 'System'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewPage(page.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4 text-gray-600" />
                              </button>
                              <button
                                onClick={() => handleOpenEdit(page.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4 text-gray-600" />
                              </button>
                              <button
                                onClick={() => {
                                  const pageSlug = getPageSectionSlug(page.slug);
                                  if (pageSlug) {
                                    setEditingPageForModal({ slug: pageSlug, name: page.title });
                                  } else {
                                    alert(`No editable sections found for this page`);
                                  }
                                }}
                                className="px-3 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium hover:bg-teal-200 transition-colors whitespace-nowrap"
                                title="Edit Full Page Content"
                              >
                                Edit Content
                              </button>
                              <button
                                onClick={() => handleDeletePage(page.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                  <p>Showing {filteredPages.length} of {totalPages} pages</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Boxes */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-l-4 border-l-teal-600 bg-teal-50">
            <CardContent className="p-6 flex gap-4">
              <Info className="w-6 h-6 text-teal-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Deployment Strategy</h3>
                <p className="text-sm text-gray-600">Published pages are cached globally via the Sovereign Edge network. Cache propagation usually takes less than 60 seconds across all regions.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-600 bg-blue-50">
            <CardContent className="p-6 flex gap-4">
              <Lock className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Access Control</h3>
                <p className="text-sm text-gray-600">Pages in 'Provider Portal' and 'Customer Portal' require active session tokens for viewing. 'Public' pages are available to unauthenticated users.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Full Page Editor Modal */}
      {editingPageForModal && (
        <FullPageEditorModal
          pageSlug={editingPageForModal.slug}
          pageName={editingPageForModal.name}
          onClose={() => setEditingPageForModal(null)}
          onSaved={() => {
            setEditingPageForModal(null);
            fetchData();
          }}
        />
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminWebsitePagesPage;
